import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Plus, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ClientDialog } from "@/components/admin/ClientDialog";
import { AdminHeader, EmptyState } from "./AdminLayout";

const StatusPill = ({ status, id }) => (
  <span data-testid={`client-status-${id}`} className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${status === "Active" ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-amber-400/40 bg-amber-400/10 text-amber-300"}`}>{status}</span>
);

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data)).catch((e) => { setClients([]); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    await api.post("/admin/clients", payload);
    toast.success(`Invite sent to ${payload.email}`);
    setOpen(false);
    load();
  };

  const resend = async (c) => {
    try {
      await api.post(`/admin/clients/${c.id}/resend-invite`);
      toast.success(`New invite sent to ${c.email}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const add = (
    <button onClick={() => setOpen(true)} data-testid="admin-add-client-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">
      <Plus className="h-4 w-4" /> Add client
    </button>
  );

  return (
    <div data-testid="admin-clients-page">
      <AdminHeader eyebrow="// portal" title="Clients" action={add}>Invite clients to their private project portal.</AdminHeader>
      {clients === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : clients.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" text="Add a client to send their portal invite." testId="admin-clients-empty-state" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
          <table data-testid="admin-clients-table" className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-white/5 font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Name", "Email", "Company", "Status", "Projects", "Added", ""].map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                  <td className="px-5 py-4 font-medium text-white">{c.name}</td>
                  <td className="px-5 py-4 text-slate-400">{c.email}</td>
                  <td className="px-5 py-4 text-slate-400">{c.company || "—"}</td>
                  <td className="px-5 py-4"><StatusPill status={c.status} id={c.id} /></td>
                  <td className="px-5 py-4 text-slate-300">{c.project_count}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-500">{format(new Date(c.created_at), "MMM d, yyyy")}</td>
                  <td className="px-5 py-4 text-right">
                    {c.status === "Invited" && (
                      <button onClick={() => resend(c)} data-testid={`client-resend-invite-${c.id}`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-cyan-400/30 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-400/10">
                        <Send className="h-3 w-3" /> Resend invite
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ClientDialog open={open} onClose={() => setOpen(false)} onSave={save} />
    </div>
  );
}
